<template>
  <div v-if="isVisible" class="survey-modal-overlay" @click.self="handleOverlayClick">
    <div class="survey-modal">
      <button class="close-button" @click="remindLater" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>

      <div class="survey-header">
        <h3>Help Us Improve ProxyMed</h3>
        <p>You've used our tool {{ viewCount }} times. We'd love your feedback!</p>
      </div>
      
      <form @submit.prevent="submitSurvey" class="survey-form">
        <!-- Was Helpful Switch -->
        <div class="form-group">
          <label class="switch-label">
            <span class="label-text">Was this tool helpful?</span>
            <div class="switch-container">
              <input 
                type="checkbox" 
                v-model="formData.wasHelpful" 
                class="switch-input"
                id="wasHelpful"
              />
              <label for="wasHelpful" class="switch"></label>
            </div>
          </label>
        </div>

        <!-- Would Use Again Switch -->
        <div class="form-group">
          <label class="switch-label">
            <span class="label-text">Would you use this tool again?</span>
            <div class="switch-container">
              <input 
                type="checkbox" 
                v-model="formData.wouldUseAgain" 
                class="switch-input"
                id="wouldUseAgain"
              />
              <label for="wouldUseAgain" class="switch"></label>
            </div>
          </label>
        </div>

        <!-- Recommendation Score -->
        <div class="form-group">
          <label class="label-text">
            How likely are you to recommend ProxyMed to a colleague? (1-10)
          </label>
          <div class="rating-container">
            <button
              v-for="score in 10"
              :key="score"
              type="button"
              @click="formData.recommendationScore = score"
              :class="['rating-button', { 'active': formData.recommendationScore === score }]"
            >
              {{ score }}
            </button>
          </div>
          <div class="rating-labels">
            <span class="rating-label-left">Not likely</span>
            <span class="rating-label-right">Very likely</span>
          </div>
        </div>

        <!-- Comments -->
        <div class="form-group">
          <label for="comments" class="label-text">
            Additional comments or suggestions
            <span class="character-count">({{ commentsLength }}/2000)</span>
          </label>
          <textarea
            id="comments"
            v-model="formData.comments"
            placeholder="Share your thoughts, suggestions, or any issues you encountered..."
            maxlength="2000"
            rows="5"
            class="textarea-input"
          ></textarea>
        </div>

        <!-- Action Buttons -->
        <div class="survey-actions">
          <button type="button" @click="remindLater" class="btn-secondary">
            Remind Me Later
          </button>
          <button 
            type="submit" 
            class="btn-primary"
            :disabled="!isFormValid || isSubmitting"
          >
            {{ isSubmitting ? 'Submitting...' : 'Submit Feedback' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed } from 'vue';

export default defineComponent({
  name: 'SurveyModal',
  props: {
    viewCount: {
      type: Number,
      required: true
    },
    recentBranchUsedId: {
      type: Number,
      required: false,
      default: null
    }
  },
  setup(props, { emit }) {
    const isVisible = ref(true);
    const isSubmitting = ref(false);

    const formData = reactive({
      wasHelpful: false,
      wouldUseAgain: false,
      recommendationScore: 0,
      comments: ''
    });

    const commentsLength = computed(() => formData.comments.length);

    const isFormValid = computed(() => {
      return formData.recommendationScore > 0;
    });

    const submitSurvey = async () => {
      if (!isFormValid.value || isSubmitting.value) return;

      isSubmitting.value = true;

      try {
        const surveyData = {
          recentBranchUsedId: props.recentBranchUsedId,
          wasHelpful: formData.wasHelpful,
          wouldUseAgain: formData.wouldUseAgain,
          recommendationScore: formData.recommendationScore,
          comments: formData.comments.trim()
        };

        console.log('📝 Submitting survey:', surveyData);

        // Emit the survey data to parent component
        emit('submit-survey', surveyData);
        localStorage.removeItem('result_view_count');
        localStorage.removeItem('currentBranchId');
       
        localStorage.setItem('fillSurvey', 'false');
        
        
        isVisible.value = false;
      } catch (error) {
        console.error('Error submitting survey:', error);
        // You might want to show an error message here
      } finally {
        isSubmitting.value = false;
      }
    };

    const remindLater = () => {
      isVisible.value = false;
      emit('remind-later');
    };

    const handleOverlayClick = () => {
      // Optional: close modal when clicking outside
      // remindLater();
    };

    return {
      isVisible,
      isSubmitting,
      formData,
      commentsLength,
      isFormValid,
      submitSurvey,
      remindLater,
      handleOverlayClick
    };
  }
});
</script>

<style scoped>
.survey-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  overflow-y: auto;
  padding: 20px;
}

.survey-modal {
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #666;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #333;
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.survey-header h3 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #1a1a1a;
  font-weight: 700;
}

.survey-header p {
  margin: 0 0 24px 0;
  color: #666;
  font-size: 14px;
}

.survey-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.label-text {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  display: block;
}

.character-count {
  font-size: 12px;
  font-weight: 400;
  color: #999;
  margin-left: 8px;
}

/* Switch Styles */
.switch-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
}

.switch-container {
  position: relative;
}

.switch-input {
  display: none;
}

.switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  background: #cbd5e1;
  border-radius: 14px;
  transition: background 0.3s;
  cursor: pointer;
}

.switch::after {
  content: '';
  position: absolute;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  top: 3px;
  left: 3px;
  transition: transform 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.switch-input:checked + .switch {
  background: #4F46E5;
}

.switch-input:checked + .switch::after {
  transform: translateX(24px);
}

/* Rating Buttons */
.rating-container {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.rating-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid #e5e7eb;
  background: white;
  color: #666;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rating-button:hover {
  border-color: #4F46E5;
  color: #4F46E5;
  transform: scale(1.1);
}

.rating-button.active {
  background: #4F46E5;
  border-color: #4F46E5;
  color: white;
  transform: scale(1.1);
}

.rating-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  margin-top: 8px;
}

/* Textarea */
.textarea-input {
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;
  min-height: 100px;
}

.textarea-input:focus {
  outline: none;
  border-color: #4F46E5;
}

.textarea-input::placeholder {
  color: #9ca3af;
}

/* Action Buttons */
.survey-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
}

.btn-primary, .btn-secondary {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;
}

.btn-primary {
  background: #4F46E5;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #4338CA;
}

.btn-primary:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.btn-secondary {
  background: #F3F4F6;
  color: #374151;
}

.btn-secondary:hover {
  background: #E5E7EB;
}

/* Mobile Responsive */
@media (max-width: 640px) {
  .survey-modal {
    padding: 24px;
  }

  .survey-header h3 {
    font-size: 20px;
  }

  .rating-container {
    gap: 6px;
  }

  .rating-button {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }

  .survey-actions {
    flex-direction: column-reverse;
  }

  .btn-primary, .btn-secondary {
    width: 100%;
  }
}

/* Scrollbar Styling */
.survey-modal::-webkit-scrollbar {
  width: 6px;
}

.survey-modal::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 3px;
}

.survey-modal::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}
</style>